import { useCallback, useEffect, useMemo, useState, type ReactElement, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  Icon,
  type IconName,
  Popover,
  Menu,
  MenuItem,
  MenuDivider,
  Button,
  PanelStack,
  Navbar,
  NavbarGroup,
  Alignment,
  Tooltip,
  useHotkeys,
  type HotkeyConfig,
} from "@blueprintjs/core";

import { BannersZone, type Banner } from "./banners";
import { ClassificationBanner } from "./classification";

import { useCases } from "../lib/hooks";
import { useRecentApps } from "../lib/recent-apps";
import { useFavoriteCases } from "../lib/favorite-cases";
import { useAuth } from "../contexts/auth-context";
import { useTheme } from "../contexts/theme-context";
import { IrisOverlay } from "./iris";
import { RailGroup } from "./rail-group";
import { CommandPalette, CmdKHint } from "./command-palette";
import { HelpPopover, buildDefaultHelpGroups } from "./help-menu";
import { UserAccountMenu, UserAccountMenuItem, TrackSwitcherButton } from "./user-menu";
import {
  WhatsNewDialog,
  WhatsNewDialogHeader,
  WhatsNewDialogFooter,
  ReleaseNoteScrollArea,
  AllCaughtUpFooter,
} from "./release-notes";
import { pathToStack } from "./panels/path-to-stack";
import type { AppPanel } from "./panels/types";
import { ApplicationPortalHost } from "./application-portal";
import { NotificationsRailButton } from "./notifications";
import { CONTROL_PANEL_ROOT } from "../lib/control-panel-routes";

/**
 * Banner inventory rendered at the top of the shell. Currently disabled —
 * to enable the example "Development preview" banner, swap to
 * `EXAMPLE_BANNERS` from `./banners`:
 *
 *   import { EXAMPLE_BANNERS } from "./banners";
 *   const SHELL_BANNERS: readonly Banner[] = EXAMPLE_BANNERS;
 */
const SHELL_BANNERS: readonly Banner[] = [];

interface RailItem {
  to: string;
  icon: IconName;
  label: string;
  /** Active only when pathname+search match exactly (no descendants). */
  exact?: boolean;
  badge?: number | string;
  /** Render as muted, non-clickable with a "soon" tag. */
  comingSoon?: boolean;
  /** Optional right-side content (shortcut hint, notification dot, etc.) — overrides badge if provided. */
  rightElement?: ReactNode;
  /** Optional content rendered absolutely at the top-right of the icon —
     compose with <NotifyDot />, a Blueprint icon (star, warning), etc. */
  iconBadge?: ReactNode;
  /** When set, the rail item renders as a button and fires this handler
     instead of navigating to `to` (used for modal launchers). */
  onClick?: () => void;
}

/**
 * Persists workspace-sidebar collapse state to localStorage under
 * `ic.shell.sidebarCollapsed`. SSR-safe: first render returns the prop
 * default (`false`, matches server); a `useEffect` hydrates from
 * localStorage; each toggle writes through. Returns `[collapsed, setter]`
 * — the setter accepts either a boolean or an updater fn so existing
 * `setCollapsed((c) => !c)` call sites keep working.
 */
const SIDEBAR_COLLAPSED_KEY = "ic.shell.sidebarCollapsed";
function useSidebarCollapsed(): [boolean, (next: boolean | ((c: boolean) => boolean)) => void] {
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (raw === "true") setCollapsed(true);
      else if (raw === "false") setCollapsed(false);
    } catch {
      // localStorage unavailable (private mode, SSR) — keep default
    }
  }, []);
  const setAndPersist = useCallback(
    (next: boolean | ((c: boolean) => boolean)) => {
      setCollapsed((prev) => {
        const value = typeof next === "function" ? next(prev) : next;
        try {
          window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, value ? "true" : "false");
        } catch {
          // localStorage unavailable — state still updates in memory
        }
        return value;
      });
    },
    [],
  );
  return [collapsed, setAndPersist];
}

export function AppShell() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // URL → stack. Memoized on pathname+search so the stack reference is stable
  // while the user interacts with the active panel — Blueprint's controlled
  // PanelStack only diffs by reference identity at each render.
  const stack = useMemo<readonly AppPanel[]>(
    () => pathToStack(pathname, search),
    [pathname, search],
  );

  // Blueprint emits onClose when the user clicks the built-in back button on
  // the active panel header. The deeper panel was reached via a <Link> (or
  // programmatic navigate), which pushed a history entry — so navigate(-1)
  // reliably pops back to the parent URL with its original query string.
  // Deep links (no history entry to pop) fall back to browser default, which
  // is acceptable for v1 of this refactor.
  const onClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const [railCollapsed, setRailCollapsed] = useSidebarCollapsed();
  const toggleRail = useCallback(() => setRailCollapsed((c) => !c), [setRailCollapsed]);

  const [isWhatsNewOpen, setIsWhatsNewOpen] = useState(false);
  const openWhatsNew = useCallback(() => setIsWhatsNewOpen(true), []);
  const closeWhatsNew = useCallback(() => setIsWhatsNewOpen(false), []);

  const [isIrisOpen, setIsIrisOpen] = useState(false);
  const toggleIris = useCallback(() => setIsIrisOpen((o) => !o), []);
  const closeIris = useCallback(() => setIsIrisOpen(false), []);
  // Let any descendant (Dashboard "Open IRIS" button, etc.) open IRIS via a
  // custom event — synthesizing the ⌘⇧U combo doesn't work (Blueprint's
  // useHotkeys ignores synthetic key events). Mirrors the app-portal pattern.
  useEffect(() => {
    const onOpen = () => setIsIrisOpen(true);
    window.addEventListener("iron:open-iris", onOpen);
    return () => window.removeEventListener("iron:open-iris", onOpen);
  }, []);

  const [appPortalOpen, setAppPortalOpen] = useState(false);
  const openAppPortal = useCallback(() => setAppPortalOpen(true), []);
  // Allow any descendant (Dashboard "View all" button, etc.) to open the
  // portal without prop-threading through PanelStack.
  useEffect(() => {
    const onOpen = () => setAppPortalOpen(true);
    window.addEventListener("iron:open-application-portal", onOpen);
    return () => window.removeEventListener("iron:open-application-portal", onOpen);
  }, []);
  const closeAppPortal = useCallback(() => setAppPortalOpen(false), []);

  // Global keybind to toggle IRIS — Cmd/Ctrl+Shift+U. Registered as a
  // Blueprint *global* hotkey (bound to the document by `useHotkeys`), so it
  // fires app-wide and shows up in the built-in hotkeys help dialog (`?`) with
  // the `label`/`group` below. (IrisOverlay handles close-while-open via the
  // same combo so it round-trips.) `allowInInput` keeps it working from any
  // focused field.
  const irisHotkeys = useMemo<HotkeyConfig[]>(
    () => [
      {
        combo: "mod+shift+u",
        label: "Toggle IRIS Assistant",
        group: "Global",
        global: true,
        allowInInput: true,
        preventDefault: true,
        onKeyDown: () => setIsIrisOpen((o) => !o),
      },
    ],
    [],
  );
  useHotkeys(irisHotkeys);

  return (
    /*
      `is-app-surface` on the two APP routes.

      Measured on the tenant: at /workspace/control-panel the settings row is
      `x=0 w=1400` on a 1400 viewport, and the dark rail beside it belongs to the
      Control Panel itself (`control-panel-app__user-sidebar-item__…`) and
      OVERLAYS rather than reserving width. Ours is a grid column
      (`var(--bp-rail-w)`), so the same surface got 1140 and every pixel after
      that differed for a reason inside the shell rather than inside the app.

      Only these two routes: the rest of the product is panels in a shell and
      should keep the rail in flow, where it cannot cover what you are reading.
    */
    <div
      data-testid="app-shell"
      className={`bp-app${railCollapsed ? " is-rail-collapsed" : ""}${isIrisOpen ? " is-iris-open" : ""}${
        /^\/(control-panel|files)(\/|$)/.test(pathname) ? " is-app-surface" : ""
      }`}
    >
      <a data-testid="app-shell-skip-link" href="#bp-main" className="bp-skip-link">Skip to content</a>
      <ClassificationBanner level="unclassified" trailer="DEMONSTRATION ENVIRONMENT · SYNTHETIC DATA" />
      <BannersZone banners={SHELL_BANNERS} />
      <Rail
        collapsed={railCollapsed}
        onToggle={toggleRail}
        onPlatformUpdates={openWhatsNew}
        onToggleIris={toggleIris}
        isIrisOpen={isIrisOpen}
        onOpenAppPortal={openAppPortal}
      />
      <main id="bp-main" className="bp-main">
        <PanelStack<AppPanel>
          stack={stack}
          onClose={onClose}
          renderActivePanelOnly={false}
          showPanelHeader={false}
        />
      </main>
      <IrisOverlay open={isIrisOpen} onClose={closeIris} railCollapsed={railCollapsed} />
      <CommandPalette />
      <ApplicationPortalHost open={appPortalOpen} onClose={closeAppPortal} />
      <WhatsNewDialog
        isOpen={isWhatsNewOpen}
        onClose={closeWhatsNew}
        header={<WhatsNewDialogHeader title="What's new in IronCase" />}
        scrollArea={
          <ReleaseNoteScrollArea>
            <AllCaughtUpFooter />
          </ReleaseNoteScrollArea>
        }
        footer={<WhatsNewDialogFooter onClose={closeWhatsNew} />}
      />
    </div>
  );
}

function Rail({
  collapsed,
  onToggle,
  onPlatformUpdates,
  onToggleIris,
  isIrisOpen,
  onOpenAppPortal,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onPlatformUpdates: () => void;
  onToggleIris: () => void;
  isIrisOpen: boolean;
  onOpenAppPortal: () => void;
}) {
  const { data: cases = [] } = useCases();
  /*
    NO APPROVALS QUERY. It existed to badge a rail entry that is now archived,
    and a fetch whose only consumer is gone is a request every user pays for and
    nobody reads. It comes back with the rail entry.
  */
  const navigate = useNavigate();
  const { recentApps, recordLaunch } = useRecentApps();
  const { favorites } = useFavoriteCases();
  const openCount = cases.filter((c) => c.status === "open").length;

  const helpGroups = useMemo(
    () => buildDefaultHelpGroups({ onPlatformUpdates }),
    [onPlatformUpdates],
  );

  // Foundry-pattern sidebar: exactly 8 fixed entries above (Home, Search,
  // Notifications, then divider, then Recent, Files, Entities, Applications),
  // a dynamic group of up to 3 recently-viewed cases in the middle, and a
  // fixed bottom trio (IRIS Chat, Help, Account). Mirrors Foundry's
  // workspace sidebar layout where the middle group is favorites/recents.
  const topPrimary: RailItem[] = [
    { to: "/", icon: "home", label: "Home", exact: true },
    { to: "/search", icon: "search", label: "Search…", comingSoon: true, rightElement: <CmdKHint /> },
  ];

  const topResources: RailItem[] = [
    { to: "/recent", icon: "history", label: "Recent", comingSoon: true },
    /*
      THE RAIL LISTS WHAT EXISTS. Cases, Approvals, Entities, Entity review and
      Warrant approval were here and their routes are now in `.archive/routes/`
      — a rail entry pointing at an archived surface falls through to the
      dashboard, which is a link that silently does the wrong thing.

      Files is the one app entry left beside the launcher, because it is one of
      the product's two apps.
    */
    { to: "/workspace/compass", icon: "folder-close", label: "Files" },
    { to: "/applications", icon: "layout-grid", label: "Applications", onClick: onOpenAppPortal },
  ];

  // Recently-launched application spots (queue of MAX_RECENT_APPS, most
  // recent on top). Shared with the home page via the `useRecentApps` store —
  // launching from either surface reorders both. Clicking records the launch
  // then navigates, so the just-opened app floats to the top next render.
  const recentAppItems: RailItem[] = recentApps.map((app) => ({
    to: app.href,
    icon: app.icon,
    label: app.name,
    onClick: () => {
      recordLaunch(app.id);
      navigate(app.href);
    },
  }));

  // Pinned (Foundry's middle rail section). Expanded rail shows the list
  // inline; collapsed rail collapses to a single pin icon whose hover-popover
  // reveals the same list. Data source is the user-driven favorites store
  // (persisted to localStorage, independent of case.flagged): resolve the
  // favorited ids against the loaded cases. Starring a case in /files surfaces
  // it here; the empty state shows until the user favorites something.
  const pinned = cases
    .filter((c) => favorites.has(c.id))
    .sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""))
    .slice(0, 8)
    .map<RailItem>((c) => ({
      to: `/cases/${c.id}`,
      icon: "folder-close",
      label: c.title ?? `Case ${c.id}`,
    }));

  // Drive open-count visibility off existing inputs so the prop is still
  // referenced and TS sees the value as "used" — kept for future use.
  void openCount;

  return (
    <nav className="bp-rail bp6-dark" aria-label="Primary navigation">
      <Navbar>
        <NavbarGroup align={Alignment.END}>
          <Button
            data-testid="app-shell-rail-toggle"
            minimal
            icon={collapsed ? "menu-open" : "menu-closed"}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
            onClick={onToggle}
          />
        </NavbarGroup>
      </Navbar>

      <div className="bp-rail-section">
        {topPrimary.map((it) => <RailLink key={it.to} item={it} collapsed={collapsed} />)}
        <NotificationsRailButton collapsed={collapsed} />
      </div>

      <div className="bp-rail-divider" />

      <div className="bp-rail-section">
        {topResources.map((it) => <RailLink key={it.to} item={it} collapsed={collapsed} />)}
      </div>

      <ApplicationsRailEntry items={recentAppItems} collapsed={collapsed} onViewAll={onOpenAppPortal} />

      <PinnedRailEntry items={pinned} collapsed={collapsed} />

      <div className="bp-rail-spacer" />

      <div className="bp-rail-footer">
        <IrisRailButton
          collapsed={collapsed}
          isOpen={isIrisOpen}
          onToggle={onToggleIris}
        />
        <HelpPopover
          groups={helpGroups}
          fill
          trigger={
            <Tooltip content="Help" placement="right" disabled={!collapsed} compact>
              <button type="button" data-testid="app-shell-help" className="bp-rail-item" aria-label="Help" title="Help">
                <Icon icon="help" />
                <span className="bp-rail-item-label">Help</span>
              </button>
            </Tooltip>
          }
        />
        <UserMenu collapsed={collapsed} />
      </div>
    </nav>
  );
}

/** Reduce an email to up to 2 uppercase initials for the principal avatar.
   "john.doe@x" → "JD", "jdoe@x" → "JD", "john@lumis-inc.com" → "J". */
function getInitials(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._\-+]/).filter(Boolean);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase() || "?";
}

/** Initials from a display name: first letters of the first two words.
   "Jonathan Agustin" → "JA". */
function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (parts[0]?.slice(0, 2) ?? "?").toUpperCase();
}

/** Best-effort human display name from an email local-part. Cognito gives us
   no display name, so we title-case the local part: "taylor.johnson@x" →
   "Taylor Johnson", "jagustin@x" → "Jagustin", "jon@x" → "Jon". */
function displayNameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  const parts = local.split(/[._\-+]/).filter(Boolean);
  if (parts.length === 0) return email;
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
}

/** Human-readable deploy environment, derived from the hostname. Surfaced in
   the account menu so it's always obvious which environment you're acting in
   (a real concern across app-dev / app-staging / app.ironcase.app). */
function envLabel(): string {
  if (typeof window === "undefined") return "Development";
  const h = window.location.hostname;
  if (h === "app.ironcase.app") return "Production";
  if (h.startsWith("app-staging")) return "Staging";
  if (h.startsWith("app-dev")) return "Development";
  return "Local";
}

function RailLink({ item, collapsed = false }: { item: RailItem; collapsed?: boolean }) {
  const { pathname, search } = useLocation();
  const active = isActive(item, pathname, search);

  const iconNode = item.iconBadge !== undefined ? (
    <span className="bp-rail-item-icon-wrap">
      <Icon icon={item.icon} />
      <span className="bp-rail-item-icon-badge">{item.iconBadge}</span>
    </span>
  ) : (
    <Icon icon={item.icon} />
  );

  const inner = (
    <>
      {iconNode}
      <span className="bp-rail-item-label">{item.label}</span>
      {item.rightElement !== undefined ? (
        <span className="bp-rail-item-right">{item.rightElement}</span>
      ) : (
        !item.comingSoon && item.badge !== undefined && item.badge !== 0 && (
          <span className="bp-rail-item-badge">{item.badge}</span>
        )
      )}
    </>
  );

  const wrap = (node: ReactNode) =>
    collapsed ? (
      <Tooltip content={item.label} placement="right" compact>
        {node as ReactElement}
      </Tooltip>
    ) : (
      node
    );

  if (item.comingSoon) {
    return wrap(
      <span
        className="bp-rail-item"
        aria-label={item.label}
        aria-disabled="true"
        title="Not yet available"
      >
        {inner}
      </span>,
    );
  }

  if (item.onClick) {
    return wrap(
      <button
        type="button"
        className={`bp-rail-item${active ? " is-active" : ""}`}
        aria-label={item.label}
        title={item.label}
        onClick={item.onClick}
      >
        {inner}
      </button>,
    );
  }

  return wrap(
    <Link
      to={item.to}
      className={`bp-rail-item${active ? " is-active" : ""}`}
      aria-label={item.label}
      title={item.label}
    >
      {inner}
    </Link>,
  );
}

// Applications rail entry — the launched-app group below the primary
// resources. Expanded: a titled "Applications" RailGroup (Foundry's
// sidebar-group: uppercase title + hover-revealed "View all" action) with an
// empty-state hint until an app is launched. Collapsed: just the app icons
// (the group header auto-hides via CSS); nothing when none, so no orphan
// divider is left behind.
function ApplicationsRailEntry({
  items,
  collapsed,
  onViewAll,
}: {
  items: RailItem[];
  collapsed: boolean;
  onViewAll: () => void;
}) {
  if (collapsed) {
    if (items.length === 0) return null;
    return (
      <>
        <div className="bp-rail-divider" />
        <div className="bp-rail-section bp-rail-section--recent-apps" aria-label="Applications">
          {items.map((it) => <RailLink key={it.to} item={it} collapsed />)}
        </div>
      </>
    );
  }
  return (
    <>
      <div className="bp-rail-divider" />
      <RailGroup
        title="Applications"
        action={
          <Button
            minimal
            small
            className="bp-rail-group-action"
            onClick={onViewAll}
            aria-label="View all applications"
            text="View all"
          />
        }
      >
        {items.length === 0 ? (
          <div className="bp-rail-empty">Launch an app to see it here</div>
        ) : (
          items.map((it) => <RailLink key={it.to} item={it} collapsed={false} />)
        )}
      </RailGroup>
    </>
  );
}

// Pinned rail entry. Expanded rail: standard RailGroup with inline list (or
// an empty-state hint). Collapsed rail: a single pin icon whose hover-popover
// reveals the same list. Mirrors Foundry's workspace sidebar pattern.
//
// Layout note: Blueprint's Popover/Tooltip wraps its child in a <span> by
// default, which strips width:100% from the trigger button and collapses it
// to content-width — wrong inside the rail's vertical-flex column. We force
// `targetTagName="div"` so the wrapper stretches and the button keeps the
// same width/centering as a RailLink.
function PinnedRailEntry({
  items,
  collapsed,
}: {
  items: RailItem[];
  collapsed: boolean;
}) {
  if (!collapsed) {
    return (
      <>
        <div className="bp-rail-divider" />
        <RailGroup title="Pinned">
          {items.length === 0 ? (
            <div className="bp-rail-empty">Star a case to pin it here</div>
          ) : (
            items.map((it) => <RailLink key={it.to} item={it} collapsed={false} />)
          )}
        </RailGroup>
      </>
    );
  }

  // Collapsed: a single folder+star "Favorites" icon whose hover-popover
  // lists the pinned cases. No inline items in this state — the rail stays
  // compact and uncluttered. When there's nothing to pin to, render
  // nothing — a folder+star with an empty popover reads as broken.
  if (items.length === 0) return null;

  return (
    <>
      <div className="bp-rail-divider" />
      <div className="bp-rail-section">
        <PinnedLauncher items={items} />
      </div>
    </>
  );
}

function PinnedLauncher({ items }: { items: RailItem[] }) {
  const trigger = (
    <button type="button" className="bp-rail-item" aria-label="Browse pinned">
      <span className="bp-rail-item-icon-wrap">
        <Icon icon="folder-open" />
        <span className="bp-rail-item-icon-star">
          <Icon icon="star" size={10} />
        </span>
      </span>
    </button>
  );

  if (items.length === 0) {
    return (
      <Tooltip
        content="No pinned cases yet"
        placement="right"
        compact
        targetTagName="div"
      >
        {trigger}
      </Tooltip>
    );
  }

  return (
    <Popover
      interactionKind="hover"
      placement="right-start"
      minimal
      hoverOpenDelay={120}
      hoverCloseDelay={150}
      targetTagName="div"
      content={
        <Menu className="bp-rail-pinned-menu">
          <MenuDivider title="Pinned" />
          {items.map((it) => (
            <PinnedMenuItem key={it.to} item={it} />
          ))}
        </Menu>
      }
    >
      {trigger}
    </Popover>
  );
}

function PinnedMenuItem({ item }: { item: RailItem }) {
  const navigate = useNavigate();
  return (
    <MenuItem
      icon={item.icon}
      text={item.label}
      onClick={() => navigate(item.to)}
    />
  );
}

// Active when the current location matches the link's path *and* its query
// shape. For exact items, the current URL must match `to` byte-for-byte after
// normalizing search-param order; otherwise the link's path must be a prefix.
function isActive(item: RailItem, pathname: string, search: string): boolean {
  const [linkPath, linkQuery = ""] = item.to.split("?");
  const linkParams = new URLSearchParams(linkQuery);
  const currentParams = new URLSearchParams(search);

  if (item.exact) {
    if (pathname !== linkPath) return false;
    // Every param the link declares must appear in the current URL with the
    // same value. Extra current params are tolerated unless the link has none —
    // in which case the URL must be "clean" so /cases?status=open doesn't also
    // activate the bare /cases link.
    if (linkParams.size === 0) return [...currentParams].length === 0;
    for (const [k, v] of linkParams) {
      if (currentParams.get(k) !== v) return false;
    }
    return true;
  }
  return pathname.startsWith(linkPath);
}

// Rail entry for IRIS — toggles the global overlay instead of navigating.
// In collapsed mode, RailLink wraps in a tooltip naturally; in expanded mode
// the label + ⌘⇧U hint reads as a shortcut.
function IrisRailButton({
  collapsed,
  isOpen,
  onToggle,
}: {
  collapsed: boolean;
  isOpen: boolean;
  onToggle: () => void;
}): ReactElement {
  const inner = (
    <button
      type="button"
      data-testid="app-shell-iris-toggle"
      className={`bp-rail-item${isOpen ? " is-active" : ""}`}
      aria-label={isOpen ? "Hide IRIS Assistant" : "Open IRIS Assistant"}
      aria-pressed={isOpen}
      onClick={onToggle}
    >
      <Icon icon="clean" />
      <span className="bp-rail-item-label">IRIS Assistant</span>
      <span className="bp-rail-item-right">
        <CmdShiftUHint />
      </span>
    </button>
  );
  if (collapsed) {
    return (
      <Tooltip content="IRIS Assistant" placement="right" compact targetTagName="div">
        {inner}
      </Tooltip>
    );
  }
  return inner;
}

function UserMenu({ collapsed = false }: { collapsed?: boolean }) {
  const { session, signOut } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();

  if (!session) return null;

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const roleAgency = [session.role, session.agencyId].filter(Boolean).join(" · ");
  const displayName = session.name ?? displayNameFromEmail(session.email);
  const initials = session.name ? initialsFromName(session.name) : getInitials(session.email);

  const trigger = (
    <button type="button" data-testid="app-shell-account" className="bp-rail-item" aria-label={`Account — ${session.email}`}>
      <span className="bp-principal-avatar bp-principal-avatar--sm" aria-hidden="true">{initials}</span>
      <span className="bp-rail-item-label">Account</span>
    </button>
  );

  return (
    <Popover
      placement="right-end"
      fill
      content={
        <UserAccountMenu
          name={displayName}
          username={session.email}
          initials={initials}
          /*
            THE PROFILE IS NOT AN APP. It is reached from the user menu, the way
            Foundry reaches its own — the Applications launcher lists products,
            and "your own account" is not one. Both of these pointed at
            /settings, which is the ORGANIZATION's settings: clicking your own
            name landed on roles and permissions for everybody.
          */
          profileHref="/profile"
          onProfileClick={(e) => {
            e.preventDefault();
            navigate("/profile");
          }}
        >
          {roleAgency && (
            <MenuItem
              disabled
              icon="shield"
              text={roleAgency}
              className="bp-user-account-menu-context"
            />
          )}
          <UserAccountMenuItem
            icon="cog"
            text="Control Panel"
            onClick={() => navigate(CONTROL_PANEL_ROOT)}
          />
          <UserAccountMenuItem
            icon={theme === "dark" ? "flash" : "moon"}
            text={theme === "dark" ? "Light mode" : "Dark mode"}
            onClick={toggleTheme}
          />
          <MenuDivider />
          <TrackSwitcherButton label="Environment" value={envLabel()} ariaHaspopup="false" />
          <MenuDivider />
          <UserAccountMenuItem icon="log-out" text="Sign out" onClick={handleSignOut} />
        </UserAccountMenu>
      }
    >
      {collapsed ? (
        <Tooltip content={`Account — ${session.email}`} placement="right" compact>
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
    </Popover>
  );
}

function CmdShiftUHint() {
  return <span className="bp6-key-combo bp6-text-muted bp6-minimal">⌘⇧U</span>;
}
